import { Extension, InputRule } from '@tiptap/core';
import { joinTextblockBackward } from '@tiptap/pm/commands';
import type { ResolvedPos } from '@tiptap/pm/model';

/**
 * 블록 경계 편집 규약 (AxRichTextEditor 전용) — 확장 4종.
 *
 * 증상이 하나 나올 때마다 키맵 확장을 새로 쌓지 말고 **이 파일의 규약을 고친다**.
 * 각 규약에는 런타임 테스트가 붙어 있다(markdown-editing.runtime.test.ts).
 *
 * 1) AxListHorizontalRule — 목록 항목 안에서 `---` 입력 시 StarterKit HorizontalRule 의
 *    nodeInputRule 이 구분선을 목록 전체 뒤로 밀어 넣는 문제를 교정한다. 목록 안이면
 *    항목을 목록 밖으로 끌어올린 뒤 그 자리에 구분선을 삽입해 목록이 커서 지점에서
 *    분할된다(노션과 동일). 목록 밖이면 null 을 반환해 기본 규칙에 위임한다.
 *
 * 2) AxTabFocusGuard — 목록 sink/lift·표 셀 이동·코드블록 들여쓰기가 모두 불가능한
 *    위치에서 Tab/Shift-Tab 이 브라우저 기본 동작(다음 폼 요소로 포커스 이동)으로
 *    흘러 편집기 밖으로 포커스가 튀는 문제를 차단한다. priority 를 기본(100)보다
 *    낮춰 기존 핸들러가 전부 실패한 뒤에만 동작하는 fallback 이다.
 *    (el-dialog 포커스 트랩은 preventDefault 를 무시하므로 컴포넌트 쪽 stopPropagation
 *    가드가 별도로 필요하다 — AxRichTextEditor.vue onRootKeydown 참고.)
 *
 * 3) AxListBackspaceLift — 목록 항목 첫 문단 시작에서 Backspace 시 기본 동작(이전
 *    항목과 병합 = 줄 자체가 사라짐) 대신 글머리만 벗겨 문단으로 만든다(줄바꿈 유지,
 *    노션 동일). 중첩 항목은 한 단계씩 내어쓴다. 입력 규칙 직후의 Backspace 는
 *    undoInputRule(방금 변환 취소)을 먼저 시도해 기존 동작을 보존한다.
 *    **2타째**(글머리가 벗겨진 문단에서 한 번 더)는 앞 항목 글자와 합쳐 한 줄로
 *    만든다 — 기본 joinBackward 는 그 문단을 앞 항목 **안**으로 밀어 넣어
 *    "글머리는 없는데 들여쓰기만 남은 줄"을 만들었다.
 *
 * 4) AxCodeBlockTab — 코드블록 안 Tab 들여쓰기 복구(아래 주석 참고).
 */

/** StarterKit HorizontalRule 의 입력 규칙과 동일한 패턴. */
const HORIZONTAL_RULE_INPUT = /^(?:---|—-|___\s|\*\*\*\s)$/;

const LIST_ITEM_TYPE_NAMES = new Set(['listItem', 'taskItem']);

const LIST_TYPE_NAMES = new Set(['bulletList', 'orderedList', 'taskList']);

/**
 * 커서가 "목록 **밖** 문단의 첫머리"이고 바로 앞 형제가 목록인가.
 *
 * AxListBackspaceLift 가 1타째에 글머리만 벗겨 만든 상태가 정확히 이 모양이다.
 */
function isAtStartOfParagraphAfterList($pos: ResolvedPos): boolean {
  if ($pos.parentOffset !== 0 || !$pos.parent.isTextblock) return false;
  if (innermostListItemName($pos)) return false;
  const depth = $pos.depth - 1;
  if (depth < 0) return false;
  const index = $pos.index(depth);
  if (index === 0) return false;
  const previous = $pos.node(depth).child(index - 1);
  return LIST_TYPE_NAMES.has(previous.type.name);
}

/** 커서를 감싼 가장 안쪽 목록 항목(listItem/taskItem) 타입명. 목록 밖이면 null. */
function innermostListItemName($pos: ResolvedPos): string | null {
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    const name = $pos.node(depth).type.name;
    if (LIST_ITEM_TYPE_NAMES.has(name)) return name;
  }
  return null;
}

export const AxListHorizontalRule = Extension.create({
  name: 'axListHorizontalRule',
  // 기본 HorizontalRule(우선순위 100)보다 먼저 평가되어야 목록 케이스를 가로챌 수 있다.
  priority: 1000,

  addInputRules() {
    const { editor } = this;
    return [
      new InputRule({
        find: HORIZONTAL_RULE_INPUT,
        handler: ({ state, range, chain }) => {
          const $from = state.doc.resolve(range.from);
          if (!innermostListItemName($from)) return null;

          // 입력 규칙 tr 에서는 매치된 `---` 텍스트만 지운다. lift 는 여기서 못 한다 —
          // prosemirror liftOutOfList 가 선행 스텝이 있는 공유 tr 에서는 내부 mapping
          // 검사에 걸려 실패하기 때문. 디스패치 직후 microtask 에서 항목을 목록 밖으로
          // 끌어올리고 구분선을 삽입한다(각각 독립 트랜잭션 — history 가 한 그룹으로 묶음).
          chain().deleteRange(range).run();
          queueMicrotask(() => {
            if (editor.isDestroyed) return;
            for (let guard = 0; guard < 12; guard += 1) {
              const name = innermostListItemName(editor.state.selection.$from);
              if (!name || !editor.commands.liftListItem(name)) break;
            }
            editor.commands.setHorizontalRule();
          });
          return undefined;
        },
      }),
    ];
  },
});

export const AxListBackspaceLift = Extension.create({
  name: 'axListBackspaceLift',
  // ListKeymap·코어 Keymap(우선순위 100)의 Backspace(병합)보다 먼저 처리한다.
  priority: 101,

  addKeyboardShortcuts() {
    return {
      Backspace: () => {
        const { editor } = this;
        // 방금 입력 규칙 변환("- " 등)의 취소가 우선.
        if (editor.commands.undoInputRule()) return true;

        const { selection } = editor.state;
        if (!selection.empty) return false;
        const { $from } = selection;

        // 2타째 — 1타째로 글머리만 벗겨진 문단에서 한 번 더 누른 경우.
        // 기본 joinBackward 는 이 문단을 앞 목록 항목 **안**으로 밀어 넣어
        // "글머리는 없는데 들여쓰기만 남은 줄"을 만든다. 대신 앞 항목의 마지막
        // 문단과 글자를 합쳐 한 줄로 만든다(확장이 없을 때의 동작과 같은 결과).
        if (isAtStartOfParagraphAfterList($from)) {
          return editor.commands.command(({ state, dispatch }) =>
            joinTextblockBackward(state, dispatch),
          );
        }

        // 1타째 — 목록 항목의 첫 문단 시작에 커서가 있을 때만.
        if ($from.parentOffset !== 0 || $from.depth < 2) return false;
        const item = $from.node($from.depth - 1);
        if (!LIST_ITEM_TYPE_NAMES.has(item.type.name)) return false;
        if ($from.index($from.depth - 1) !== 0) return false;
        return editor.commands.liftListItem(item.type.name);
      },
    };
  },
});

/**
 * 코드블록 Tab 들여쓰기 복구.
 *
 * `codeBlock: { enableTabIndentation: true }` 로 켜 두었는데도 실제로는 동작하지
 * 않았다 — `@tiptap/extension-code-block` 이 `insertContent(' '.repeat(tabSize))` 로
 * 공백을 넣는데, `tiptap-markdown` 이 `insertContentAt` 을 마크다운 파서로 갈아끼워
 * **공백만 있는 문자열이 빈 조각으로 파싱돼 사라진다**. 커맨드는 true 를 돌려주고
 * preventDefault 까지 되므로 완전 무증상이었다(내어쓰기 Shift-Tab 만 멀쩡했다).
 *
 * 그래서 CodeBlock(우선순위 100)보다 먼저 가로채 `tr.insertText` 로 직접 넣는다.
 * 이 편집기에서 내용을 넣을 때 문자열 `insertContent` 를 쓰면 안 된다는 규약이
 * 여기서 나온다(`*굵게*` 가 기울임으로 파싱되는 것도 같은 원인).
 */
export const AxCodeBlockTab = Extension.create<{ tabSize: number }>({
  name: 'axCodeBlockTab',
  // CodeBlock 의 Tab(우선순위 100)보다 먼저 평가되어야 한다.
  priority: 101,

  addOptions() {
    return { tabSize: 4 };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        const { editor } = this;
        if (!editor.isEditable || !editor.isActive('codeBlock')) return false;
        const indent = ' '.repeat(this.options.tabSize || 4);
        return editor.commands.command(({ tr, state, dispatch }) => {
          if (dispatch) {
            const { from, to } = state.selection;
            tr.insertText(indent, from, to);
          }
          return true;
        });
      },
    };
  },
});

export const AxTabFocusGuard = Extension.create({
  name: 'axTabFocusGuard',
  // ListItem·TaskItem·Table·CodeBlock 의 Tab 처리(우선순위 100)가 모두 실패한 뒤의 fallback.
  priority: 50,

  addKeyboardShortcuts() {
    return {
      Tab: () => this.editor.isEditable,
      'Shift-Tab': () => this.editor.isEditable,
    };
  },
});
